'use client'

import * as React from 'react'

import { cn } from '../../../lib/utils'
import Icon from '../Icon'
import { Input } from '../Input'
import { Textarea } from '../Textarea'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../Collapsible'
import {
  composerFieldShellClassName,
  composerTextareaClassName,
  resizeComposerField,
} from '../../../lib/composerFieldStyles'
import { getInputCaretCoordinates } from '../../../lib/getInputCaretCoordinates'
import { Popover, PopoverAnchor, PopoverContent } from '../Popover'

export type LiquidContextField = {
  path: string
  label: string
  type?: string
  description?: string
  children?: LiquidContextField[]
}

export type LiquidInputVariant = 'default' | 'inline'

export interface LiquidInputProps {
  id: string
  label?: string
  helpText?: string
  value: string
  onChange: (value: string) => void
  isMultiLineEnabled?: boolean
  contextFields?: LiquidContextField[]
  sampleContext?: Record<string, unknown>
  mode?: 'expression' | 'template'
  disabled?: boolean
  placeholder?: string
  className?: string
  /** Borderless composer styling; hides context browser and sample payload. */
  variant?: LiquidInputVariant
}

function flattenFields(
  fields: LiquidContextField[],
): Array<LiquidContextField & { fullPath: string }> {
  const result: Array<LiquidContextField & { fullPath: string }> = []

  for (const field of fields) {
    result.push({ ...field, fullPath: field.path })
    if (field.children?.length) {
      result.push(...flattenFields(field.children))
    }
  }

  return result
}

function getIncompleteLiquidRange(
  value: string,
  cursor: number,
): { start: number; end: number } | null {
  const before = value.slice(0, cursor)
  const openIndex = before.lastIndexOf('{{')
  if (openIndex === -1) {
    return null
  }

  let inner = before.slice(openIndex + 2)
  const trimmedInner = inner.replace(/\s*\}+\s*$/, '')

  if (inner !== trimmedInner && trimmedInner.endsWith('.')) {
    inner = trimmedInner
  } else if (inner.includes('}}')) {
    return null
  }

  return { start: openIndex, end: cursor }
}

function consumeTrailingPartialCloses(value: string, start: number): number {
  let index = start

  while (index < value.length && value[index] === ' ') {
    index++
  }

  let consumed = 0
  while (index < value.length && value[index] === '}' && consumed < 2) {
    index++
    consumed++
  }

  return index
}

function getReplacementEnd(
  value: string,
  replaceEnd: number,
  path: string,
): number {
  let index = replaceEnd

  while (index < value.length && value[index] === ' ') {
    index++
  }

  const pathSuffix = path.includes('.')
    ? path.slice(path.lastIndexOf('.') + 1)
    : path

  if (
    pathSuffix &&
    value.slice(index, index + pathSuffix.length) === pathSuffix
  ) {
    index += pathSuffix.length
    return consumeTrailingPartialCloses(value, index)
  }

  return consumeTrailingPartialCloses(value, replaceEnd)
}

function insertLiquidPath(
  value: string,
  path: string,
  selectionStart: number,
  selectionEnd: number,
): { nextValue: string; cursor: number } {
  const insertion = `{{ ${path} }}`
  const incompleteRange = getIncompleteLiquidRange(value, selectionStart)

  if (incompleteRange) {
    const replaceEnd = getReplacementEnd(value, incompleteRange.end, path)
    const nextValue =
      value.slice(0, incompleteRange.start) +
      insertion +
      value.slice(replaceEnd)
    return { nextValue, cursor: incompleteRange.start + insertion.length }
  }

  const start = selectionStart
  const end = selectionEnd
  const nextValue = value.slice(0, start) + insertion + value.slice(end)
  return { nextValue, cursor: start + insertion.length }
}

function liquidSuggestionItemClass(isInline: boolean) {
  return cn(
    'flex w-full flex-col text-left transition-colors',
    isInline
      ? 'rounded-sm px-1.5 py-1 text-[11px] text-foreground/80 hover:bg-muted/60'
      : 'rounded-md px-2 py-1.5 text-xs hover:bg-accent/70',
  )
}

function getActiveLiquidFilter(value: string, cursor: number): string | null {
  const before = value.slice(0, cursor)
  const openIndex = before.lastIndexOf('{{')
  if (openIndex === -1) {
    return null
  }

  const afterOpen = before.slice(openIndex + 2)
  if (afterOpen.includes('}}')) {
    return null
  }

  return afterOpen.trim()
}

function getActiveLiquidAnchorIndex(
  value: string,
  cursor: number,
): number | null {
  const incompleteRange = getIncompleteLiquidRange(value, cursor)
  if (!incompleteRange) {
    return null
  }

  const inner = value.slice(incompleteRange.start + 2, cursor)
  const leadingWhitespace = inner.length - inner.trimStart().length
  return incompleteRange.start + 2 + leadingWhitespace
}

const ContextFieldNode: React.FC<{
  field: LiquidContextField
  onSelect: (path: string) => void
  level?: number
}> = ({ field, onSelect, level = 0 }) => {
  const hasChildren = Boolean(field.children?.length)
  const indent = level > 0 ? 'ml-2 border-l border-accent/30 pl-2' : ''

  if (!hasChildren) {
    return (
      <button
        type="button"
        className={cn(
          'flex w-full items-start gap-2 rounded-md px-2 py-1 text-left text-2xs hover:bg-accent/70',
          indent,
        )}
        onMouseDown={(e) => {
          e.preventDefault()
          onSelect(field.path)
        }}
      >
        <Icon name="Braces" size={12} className="mt-0.5 shrink-0" />
        <span className="min-w-0">
          <span className="block font-mono">{field.path}</span>
          <span className="text-muted-foreground">{field.label}</span>
        </span>
      </button>
    )
  }

  return (
    <Collapsible className={indent}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-2xs hover:bg-accent/70"
        >
          <Icon name="ChevronRight" size={10} className="shrink-0" />
          <span className="font-medium">{field.label}</span>
          <span className="font-mono text-muted-foreground">{field.path}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-0.5 pb-1">
        {field.children?.map((child) => (
          <ContextFieldNode
            key={child.path}
            field={child}
            onSelect={onSelect}
            level={level + 1}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

export const LiquidInput: React.FC<LiquidInputProps> = ({
  id,
  label,
  helpText,
  value,
  onChange,
  isMultiLineEnabled = false,
  contextFields = [],
  sampleContext,
  mode = 'template',
  disabled = false,
  placeholder,
  className,
  variant = 'default',
}) => {
  const isInline = variant === 'inline'
  const fieldWrapperRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const cursorRef = React.useRef(0)
  const [showSuggestions, setShowSuggestions] = React.useState(false)
  const [activeFilter, setActiveFilter] = React.useState('')
  const [showSample, setShowSample] = React.useState(false)
  const [caretAnchor, setCaretAnchor] = React.useState({
    top: 0,
    left: 0,
    height: 20,
  })

  const updateCaretAnchor = React.useCallback(() => {
    const element = inputRef.current
    const container = fieldWrapperRef.current
    if (!element || !container || typeof window === 'undefined') {
      return
    }

    const cursor =
      element.selectionStart ?? cursorRef.current ?? element.value.length
    const anchorIndex =
      getActiveLiquidAnchorIndex(element.value, cursor) ?? cursor
    const coords = getInputCaretCoordinates(element, anchorIndex)
    const elementRect = element.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    setCaretAnchor({
      top: elementRect.top - containerRect.top + coords.top,
      left: elementRect.left - containerRect.left + coords.left,
      height: coords.height,
    })
  }, [])

  const flatFields = React.useMemo(
    () => flattenFields(contextFields),
    [contextFields],
  )

  const suggestions = React.useMemo(() => {
    if (!activeFilter) {
      return flatFields.slice(0, 12)
    }

    const query = activeFilter.toLowerCase()
    return flatFields
      .filter(
        (field) =>
          field.fullPath.toLowerCase().includes(query) ||
          field.label.toLowerCase().includes(query),
      )
      .slice(0, 12)
  }, [activeFilter, flatFields])

  const syncCursor = (element: HTMLInputElement | HTMLTextAreaElement) => {
    cursorRef.current = element.selectionStart ?? element.value.length
    updateCaretAnchor()
  }

  const handleSelectPath = React.useCallback(
    (path: string) => {
      const element = inputRef.current
      const selectionStart =
        element?.selectionStart ?? cursorRef.current ?? value.length
      const selectionEnd = element?.selectionEnd ?? selectionStart

      const { nextValue, cursor } = insertLiquidPath(
        value,
        path,
        selectionStart,
        selectionEnd,
      )

      onChange(nextValue)
      setShowSuggestions(false)
      setActiveFilter('')
      cursorRef.current = cursor

      requestAnimationFrame(() => {
        if (!element) {
          return
        }
        element.focus()
        element.setSelectionRange(cursor, cursor)
      })
    },
    [onChange, value],
  )

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const nextValue = e.target.value
    onChange(nextValue)
    syncCursor(e.target)

    const filter = getActiveLiquidFilter(
      nextValue,
      e.target.selectionStart ?? nextValue.length,
    )
    setActiveFilter(filter ?? '')
    setShowSuggestions(filter !== null)
    if (filter !== null) {
      requestAnimationFrame(updateCaretAnchor)
    }
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  const inlineFieldClassName = cn(composerTextareaClassName, className)

  React.useEffect(() => {
    if (!isInline || !isMultiLineEnabled) return
    const element = inputRef.current
    if (element && 'scrollHeight' in element) {
      resizeComposerField(element as HTMLTextAreaElement)
    }
    if (showSuggestions) {
      updateCaretAnchor()
    }
  }, [value, isInline, isMultiLineEnabled, showSuggestions, updateCaretAnchor])

  const sharedInputProps = {
    id,
    value,
    disabled,
    placeholder,
    className: cn('text-sm', !isInline && className),
    handleChange: handleInputChange,
    onKeyDown: handleKeyDown,
    onBlur: () => {
      window.setTimeout(() => setShowSuggestions(false), 150)
    },
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      syncCursor(e.currentTarget)
      const filter = getActiveLiquidFilter(
        e.currentTarget.value,
        e.currentTarget.selectionStart ?? e.currentTarget.value.length,
      )
      setActiveFilter(filter ?? '')
      setShowSuggestions(filter !== null)
    },
    onClick: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      syncCursor(e.currentTarget)
    },
    onKeyUp: (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      syncCursor(e.currentTarget)
    },
  }

  return (
    <div className={cn('w-full', !isInline && 'space-y-2')}>
      {label && (
        <div className="text-muted-foreground mb-1 h-5 text-sm">{label}</div>
      )}

      <Popover open={showSuggestions && suggestions.length > 0}>
        <div
          ref={fieldWrapperRef}
          className={cn(
            isInline && isMultiLineEnabled && composerFieldShellClassName,
            'relative w-full',
          )}
        >
          {isMultiLineEnabled ? (
            isInline ? (
              <textarea
                id={id}
                value={value}
                disabled={disabled}
                placeholder={placeholder}
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                rows={1}
                className={inlineFieldClassName}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={sharedInputProps.onBlur}
                onFocus={sharedInputProps.onFocus}
                onClick={sharedInputProps.onClick}
                onKeyUp={sharedInputProps.onKeyUp}
                onScroll={updateCaretAnchor}
                onInput={(e) => {
                  resizeComposerField(e.target as HTMLTextAreaElement)
                  updateCaretAnchor()
                }}
              />
            ) : (
              <Textarea
                {...sharedInputProps}
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                rows={4}
                onScroll={updateCaretAnchor}
              />
            )
          ) : (
            <Input
              {...sharedInputProps}
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              leftIcon="Braces"
            />
          )}
          <PopoverAnchor asChild>
            <div
              aria-hidden
              className="pointer-events-none absolute h-px w-px"
              style={{
                top: caretAnchor.top + caretAnchor.height,
                left: caretAnchor.left,
              }}
            />
          </PopoverAnchor>
        </div>
        <PopoverContent
          className={cn(
            isInline
              ? 'w-60 max-w-[calc(100vw-2rem)] border-border/60 p-0.5 shadow-sm data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100'
              : 'w-60 max-w-[calc(100vw-2rem)] p-1',
          )}
          align="start"
          side="bottom"
          sideOffset={isInline ? 2 : 4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            className={cn(
              'overflow-y-auto',
              isInline ? 'max-h-40' : 'max-h-48',
            )}
          >
            {suggestions.map((field) => (
              <button
                key={field.fullPath}
                type="button"
                className={liquidSuggestionItemClass(isInline)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelectPath(field.fullPath)
                }}
              >
                {isInline ? (
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="truncate font-mono text-[11px] leading-4">
                      {field.fullPath}
                    </div>
                    <div className="truncate text-[9px] leading-3 text-muted-foreground/70">
                      {field.label}
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="font-mono text-xs">{field.fullPath}</span>
                    <span className="text-muted-foreground text-2xs">
                      {field.label}
                    </span>
                  </>
                )}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {mode === 'expression' && (
        <div className="flex flex-wrap gap-1">
          {['==', '!=', 'contains'].map((operator) => (
            <button
              key={operator}
              type="button"
              className="rounded-md border px-2 py-0.5 font-mono text-2xs hover:bg-accent"
              disabled={disabled}
              onClick={() => {
                const element = inputRef.current
                const operatorText = ` ${operator} `
                if (!element) {
                  onChange(`${value}${operatorText}`)
                  return
                }
                const selectionStart =
                  element.selectionStart ?? cursorRef.current ?? value.length
                const selectionEnd = element.selectionEnd ?? selectionStart
                const nextValue =
                  value.slice(0, selectionStart) +
                  operatorText +
                  value.slice(selectionEnd)
                const cursor = selectionStart + operatorText.length
                onChange(nextValue)
                cursorRef.current = cursor
                requestAnimationFrame(() => {
                  element.focus()
                  element.setSelectionRange(cursor, cursor)
                })
              }}
            >
              {operator}
            </button>
          ))}
        </div>
      )}

      {!isInline && contextFields.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 text-2xs text-muted-foreground hover:text-foreground"
            >
              <Icon name="List" size={12} />
              Browse event context
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 max-h-48 overflow-y-auto rounded-md border bg-accent/20 p-2">
            {contextFields.map((field) => (
              <ContextFieldNode
                key={field.path}
                field={field}
                onSelect={handleSelectPath}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {!isInline && sampleContext && (
        <Collapsible open={showSample} onOpenChange={setShowSample}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 text-2xs text-muted-foreground hover:text-foreground"
            >
              <Icon name="FileText" size={12} />
              Sample event payload
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <pre className="max-h-40 overflow-auto rounded-md border bg-muted/40 p-2 font-mono text-2xs">
              {JSON.stringify(sampleContext, null, 2)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      )}

      {helpText && (
        <div className="text-muted-foreground text-xs">{helpText}</div>
      )}
    </div>
  )
}
